import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TlkSlideToggleComponent } from '@itero/ui-toolkit-angular/slide-toggle';
import { ApplianceTypeEnums } from '@modules/appliance-details/models/appliance-type.enum';
import { ApplianceDetailsState } from '@modules/appliance-details/state/appliance-details.store';
import {
	RxIntuitiveApplianceVisibilityService,
	RxIntuitiveApplianceFieldsVisibility
} from '@modules/rx-intuitive-appliance-details/rx-intuitive-appliance-visibility.service';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxProcedureTypeControlComponent } from '@shared/components/rx-procedure-type-control/rx-procedure-type-control.component';
import { SharedModule } from '@shared/shared.module';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { IntuitiveApplianceDetailsFacade, IntuitiveApplianceDetailsFormState } from '../../rx-intuitive-appliance-details.facade';

@Component({
	selector: 'rx-intuitive-appliance-details',
	templateUrl: './rx-intuitive-appliance-details.component.html',
	styleUrls: ['./rx-intuitive-appliance-details.component.scss'],
	providers: [IntuitiveApplianceDetailsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		SharedModule,
		TranslateModule,
		TlkSlideToggleComponent,
		TlkCardComponent,
		RxProcedureTypeControlComponent
	]
})
export class RxIntuitiveApplianceDetailsComponent extends BaseDestroyableComponent implements OnInit {
	private isReadOnly: boolean;

	applianceTypes$ = this.facade.applianceTypes$;
	readonly applianceFieldsVisibility$ = this.rxIntuitiveApplianceVisibilityService.fields$;
	isReadOnly$: Observable<boolean> = this.facade.isReadonly$.pipe(
		tap(isReadOnly => (this.isReadOnly = isReadOnly)),
		shareReplay({ refCount: true, bufferSize: 1 })
	);
	isApplianceUpperReadonly$ = this.facade.isApplianceUpperReadonly$;
	isApplianceLowerReadonly$ = this.facade.isApplianceLowerReadonly$;
	procedureTitle$ = this.facade.procedureTitle$;

	form = this.formBuilder.nonNullable.group<IntuitiveApplianceDetailsFormState>({
		applianceType: { Id: ApplianceTypeEnums.Empty, Name: '' },
		isUpperChecked: false,
		isLowerChecked: false
	});

	/** Emits hidden fields patch when visibility changes. Readonly prevents clearing. */
	private readonly clearHiddenFieldsOnVisibilityChange$: Observable<Partial<ApplianceDetailsState>> = combineLatest([
		this.facade.isReadonly$,
		this.rxIntuitiveApplianceVisibilityService.fields$
	]).pipe(
		distinctUntilChanged(
			(
				[prevReadOnly, prevVis]: [boolean, RxIntuitiveApplianceFieldsVisibility],
				[currReadOnly, currVis]: [boolean, RxIntuitiveApplianceFieldsVisibility]
			) =>
				prevReadOnly === currReadOnly &&
				prevVis.applianceType === currVis.applianceType &&
				prevVis.upperLowerCard === currVis.upperLowerCard
		),
		map(([isReadOnly, visibility]) => {
			if (isReadOnly) {
				return {};
			}

			const patch: Partial<ApplianceDetailsState> = {};

			if (!visibility.applianceType) {
				patch.applianceType = { Id: ApplianceTypeEnums.Empty, Name: '' };
			}
			if (!visibility.upperLowerCard) {
				patch.isUpperChecked = false;
				patch.isLowerChecked = false;
			}

			return patch;
		})
	);

	get applianceTypeControl() {
		return this.form.controls.applianceType;
	}

	get isUpperControl() {
		return this.form.controls.isUpperChecked;
	}

	get isLowerControl() {
		return this.form.controls.isLowerChecked;
	}

	constructor(
		private readonly facade: IntuitiveApplianceDetailsFacade,
		private readonly formBuilder: FormBuilder,
		private readonly rxIntuitiveApplianceVisibilityService: RxIntuitiveApplianceVisibilityService
	) {
		super();
	}

	ngOnInit(): void {
		this.loadFromStore();
		this.subscribeToUserChanges();
		this.subscribeToClearHiddenFields();
	}

	switchOffControl(event: boolean, control: FormControl) {
		if (!event && !this.isReadOnly) {
			control.patchValue(false);
		}
	}

	private loadFromStore(): void {
		this.form.patchValue(this.facade.storeState, { emitEvent: false });
	}

	private subscribeToUserChanges(): void {
		this.form.valueChanges
			.pipe(
				tap(() => this.facade.updateApplianceDetails(this.form.getRawValue())),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToClearHiddenFields(): void {
		this.clearHiddenFieldsOnVisibilityChange$
			.pipe(
				tap(patch => {
					if (Object.keys(patch).length > 0) {
						this.form.patchValue(patch);
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}
}
